import { ref, shallowRef, getCurrentInstance } from 'vue';
import axios from 'axios';
import { loadRoundDataRefFromUrl } from '../utils/demoRoundData';
import { isCancellation, pollRoundUrl, reportRoundMissing } from '../utils/round_url_poller';

// Backend Demo statuses before any analysis has been queued.
const NEVER_ANALYZED_STATUSES = ['created', 'uploaded'];
const VIEWER_RECHECK_MS = 15000;

export function useDemoLoader(route, store) {
  const demoStatus = ref('ready');
  const roundData = shallowRef(null);
  const mapInfo = ref(null);
  const currentRound = ref(1);
  const totalRounds = ref(24);
  const roundInfo = ref(null);
  const mapImageSrc = ref(null);
  const showLowerMap = ref(false);
  const isLoadingRound = ref(false);
  // Distinct from isLoadingRound: the round exists but is being built, which is
  // a wait of seconds rather than the usual fetch. The timeline already dims
  // itself on isLoadingRound; this is what lets the UI say why.
  const isPreparingRound = ref(false);
  const roundLoadFailed = ref(false);

  // /matches/:uuid/review has no Demo row: rsparse only, nothing to analyze or subscribe to.
  const viewerBase = route.params.uuid ? `/matches/${route.params.uuid}` : `/demos/${route.params.id}`;
  const axiosAbortControllers = ref([]);
  let roundLoadGeneration = 0;
  let recheckTimer = null;
  let stopped = false;
  let roundReprocessing = false;

  // rsparse pushes nothing on DemoChannel, so a match still parsing is checked again.
  // One pending check at most, and none once the page is gone.
  function scheduleRecheck(check = loadDemoData) {
    clearTimeout(recheckTimer);
    if (!stopped) recheckTimer = setTimeout(check, VIEWER_RECHECK_MS);
  }
  const websocket = ref(null);
  const subscription = ref(null);

  // Route query handling
  if (route.query.round) {
    currentRound.value = parseInt(route.query.round);
  }

  function subscribeToDemo() {
    const instance = getCurrentInstance();
    const cable = instance.appContext.config.globalProperties.$cable;

    if (!websocket.value) {
      websocket.value = cable.connect();
    }
    if (!subscription.value) {
      subscription.value = websocket.value.subscriptions.create(
        { channel: 'DemoChannel', id: route.params.id },
        {
          received: (data) => {
            demoStatus.value = data.status;
            // loadDemoData loads the round once the demo is ready; running both at once raced.
            if (data.status === 'ready') loadDemoData();
          },
        },
      );
    }
  }

  function analyzeDemo() {
    const abortController = new AbortController();
    axiosAbortControllers.value.push(abortController);

    axios({
      method: 'post',
      url: `/demos/${route.params.id}/analyze`,
      headers: {
        'X-TEAM-ID': store.state.activeTeam?.id,
      },
      signal: abortController.signal,
    })
      .then(({ data }) => {
        const index = axiosAbortControllers.value.indexOf(abortController);
        if (index > -1) {
          axiosAbortControllers.value.splice(index, 1);
        }

        demoStatus.value = data.status;
        if (data.status === 'ready') loadDemoData();
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'CanceledError' || (error.code === 'ERR_CANCELED')) {
          console.log('Analyze demo request cancelled:', error.message);
          return;
        }
        console.log(error);
      });
  }

  async function loadDemoData() {
    // Support test.buf for testing
    if (route.params.id === 'test' || route.params.id === '4') {
      if (route.params.id === '4') {
        mapInfo.value = {
          name: 'de_overpass',
          friendly_name: 'Overpass',
          x_0: -4831,
          y_0: 1781,
          scale: 5.2,
          has_lower_map: false,
        };
        totalRounds.value = 24;
        roundInfo.value = {};
        demoStatus.value = 'ready';
        mapImageSrc.value = `/maps/${mapInfo.value.name}_radar.svg`;
        loadRoundData();
        return;
      }
      return;
    }

    const abortController = new AbortController();
    axiosAbortControllers.value.push(abortController);

    await axios({
      method: 'get',
      // The viewer's own demo, built from rsparse; restrat keeps the legacy row (/demos/:id).
      url: `${viewerBase}/viewer`,
      headers: {
        'X-TEAM-ID': store.state.activeTeam?.id,
      },
      signal: abortController.signal,
    })
      .then(({ data }) => {
        const index = axiosAbortControllers.value.indexOf(abortController);
        if (index > -1) {
          axiosAbortControllers.value.splice(index, 1);
        }

        // Only a never-analyzed demo is kicked off; terminal statuses (failed,
        // map_not_found, broken, ...) are shown, never re-queued on a page view.
        const unparsed = !data?.parsed_data && data.status !== 'ready';
        if (data.status === 'analyzing' || unparsed) {
          demoStatus.value = data.status;
          if (!route.params.uuid && NEVER_ANALYZED_STATUSES.includes(data.status)) analyzeDemo();
          if (data.status === 'analyzing') scheduleRecheck();
          return;
        }
        mapInfo.value = data?.cs_map;
        totalRounds.value = data.parsed_data?.total_rounds || 24;
        roundInfo.value = data.parsed_data?.round_info || {};
        demoStatus.value = data.status;
        mapImageSrc.value = `/maps/${mapInfo.value.name}_radar.svg`;
        loadRoundData();
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'CanceledError' || (error.code === 'ERR_CANCELED')) {
          console.log('Demo data request cancelled:', error.message);
          return;
        }
        // Below the tier a match-keyed viewer needs; the backend answers 403.
        if (error.response?.status === 403) demoStatus.value = 'failed';
        console.log(error);
      });
  }

  async function loadBufFile(filePath, onDecoded) {
    try {
      await loadRoundDataRefFromUrl(roundData, filePath);

      if (onDecoded) {
        onDecoded();
      }
    } catch (error) {
      console.error('Error loading buf file:', error);
      console.error('Error details:', error.message, error.stack);
    }
  }

  function clearRoundData() {
    if (roundData.value) {
      if (roundData.value.frames) {
        roundData.value.frames = null;
      }
      if (roundData.value.kills) {
        roundData.value.kills = null;
      }
      if (roundData.value.damages) {
        roundData.value.damages = null;
      }
      if (roundData.value.grenades) {
        roundData.value.grenades = null;
      }
      if (roundData.value.infernos) {
        roundData.value.infernos = null;
      }
      if (roundData.value.bombEvents) {
        roundData.value.bombEvents = null;
      }
      roundData.value = null;
    }
  }

  async function decodeFromURL(url, stillCurrent, onDecoded) {
    const abortController = new AbortController();
    axiosAbortControllers.value.push(abortController);

    try {
      const bufferUrl = import.meta.env.VITE_ENVIRONMENT !== 'local.' ? url : '/round-7.buf';
      const decoded = { value: null };
      await loadRoundDataRefFromUrl(decoded, bufferUrl, abortController.signal);
      if (!stillCurrent()) return 'stale';

      roundData.value = decoded.value;
      if (onDecoded) {
        onDecoded();
      }
      return 'ok';
    } catch (error) {
      if (isCancellation(error)) {
        // Navigating away is not evidence about the artifact. Reporting it as
        // missing would release a registry row that is perfectly good.
        console.log('Request cancelled:', error.message);
        return 'aborted';
      }
      console.error('Error loading files:', error);
      return 'failed';
    } finally {
      const index = axiosAbortControllers.value.indexOf(abortController);
      if (index > -1) {
        axiosAbortControllers.value.splice(index, 1);
      }
    }
  }

  // A cold round is materialized on first view: Rails answers 202 while that
  // happens and 200 with a URL once it lands. See utils/round_url_poller.js.
  // Resolves to a URL, or null if the caller should stop (cancelled, failed, or
  // superseded). Page-level state belongs to the latest load only.
  async function fetchRoundUrl(roundAtStart, stillCurrent) {
    const abortController = new AbortController();
    axiosAbortControllers.value.push(abortController);

    try {
      const result = await pollRoundUrl({
        basePath: viewerBase,
        round: roundAtStart,
        teamId: store.state.activeTeam?.id,
        signal: abortController.signal,
        isCurrent: stillCurrent,
        onPreparing: () => { isPreparingRound.value = true; },
      });

      // A round that exists ends a round re-processing wait, and only that one.
      if (result.kind === 'ready') {
        if (roundReprocessing) demoStatus.value = 'ready';
        roundReprocessing = false;
        return result.url;
      }
      // Nothing left to rebuild from. Say so rather than spin.
      if (result.kind === 'gone') demoStatus.value = 'failed';
      // A re-parse takes minutes: the full-page processing state owns the wait
      // and the DemoChannel push brings the page back.
      if (result.kind === 'processing') {
        roundReprocessing = true;
        demoStatus.value = 'analyzing';
        scheduleRecheck(() => loadRoundData());
      }
      return null;
    } finally {
      const index = axiosAbortControllers.value.indexOf(abortController);
      if (index > -1) axiosAbortControllers.value.splice(index, 1);
    }
  }

  function reportMissing(roundNumber) {
    return reportRoundMissing({
      basePath: viewerBase,
      round: roundNumber,
      teamId: store.state.activeTeam?.id,
    });
  }

  async function loadRoundData(onDecoded) {
    isLoadingRound.value = true;
    isPreparingRound.value = false;
    roundLoadFailed.value = false;
    const generation = ++roundLoadGeneration;
    try {
      // Support test.buf for testing
      if (route.params.id === 'test') {
        await loadBufFile('/test.buf', () => {
          isLoadingRound.value = false;
          if (onDecoded) onDecoded();
        });
        return;
      }
      if (route.params.id === '4') {
        await loadBufFile('/test.buf', () => {
          isLoadingRound.value = false;
          if (onDecoded) onDecoded();
        });
        return;
      }

      const roundAtStart = currentRound.value;
      const stillCurrent = () => generation === roundLoadGeneration && currentRound.value === roundAtStart;
      const url = await fetchRoundUrl(roundAtStart, stillCurrent);

      if (!stillCurrent()) return;

      isPreparingRound.value = false;

      if (demoStatus.value !== 'ready') {
        isLoadingRound.value = false;
        return;
      }

      if (!url) {
        failRoundLoad();
        return;
      }

      const decoded = await decodeFromURL(url, stillCurrent, () => {
        isLoadingRound.value = false;
        if (onDecoded) onDecoded();
      });

      // A genuine failure is evidence about roundAtStart's artifact - server
      // truth, so it is reported even when the viewer has already scrubbed
      // away. The registry only knows an artifact probably exists; the browser
      // is where we find out it does not, and skipping the report because the
      // screen moved on would leave the next visit presigning the same dead
      // object. An abort says nothing about the artifact and reports nothing.
      if (decoded === 'failed') {
        await reportMissing(roundAtStart);
      }

      // Page state, by contrast, belongs to whatever is on screen now.
      if (decoded === 'aborted' || !stillCurrent()) return;

      if (decoded === 'failed') failRoundLoad();
    } catch (error) {
      isLoadingRound.value = false;
      isPreparingRound.value = false;
      console.error('Error loading round data:', error);
    }
  }

  function failRoundLoad() {
    clearRoundData();
    isLoadingRound.value = false;
    roundLoadFailed.value = true;
  }

  function toggleMaps() {
    if (!mapInfo.value?.has_lower_map) return;
    showLowerMap.value = !showLowerMap.value;
    mapImageSrc.value = showLowerMap.value
      ? `/maps/${mapInfo.value.name}_lower_radar.svg`
      : `/maps/${mapInfo.value.name}_radar.svg`;
  }

  function cleanup() {
    roundLoadGeneration++;
    stopped = true;
    clearTimeout(recheckTimer);

    // Cancel all pending axios requests
    axiosAbortControllers.value.forEach((abortController) => {
      if (abortController && typeof abortController.abort === 'function') {
        abortController.abort('Component unmounting');
      }
    });
    axiosAbortControllers.value = [];

    // Clean up WebSocket subscription
    if (subscription.value) {
      subscription.value.unsubscribe();
      subscription.value = null;
    }

    // Clean up WebSocket connection
    if (websocket.value) {
      websocket.value.disconnect();
      websocket.value = null;
    }

    clearRoundData();
    roundInfo.value = null;
    mapInfo.value = null;
  }

  return {
    demoStatus,
    roundData,
    mapInfo,
    currentRound,
    totalRounds,
    roundInfo,
    mapImageSrc,
    showLowerMap,
    isLoadingRound,
    isPreparingRound,
    roundLoadFailed,
    subscribeToDemo,
    loadDemoData,
    loadRoundData,
    toggleMaps,
    cleanup,
  };
}
